import { useEffect, useRef } from 'react';
import { logger, useCurrentUserProfile } from '@lark-apaas/client-toolkit-lite';
import { getCurrentUserId } from '@/lib/user-storage';

/**
 * 用户身份守卫：检测到明确的账号切换时整页 reload，
 * 让所有业务 hooks 从新账号的 userStorage 重新初始化。
 *
 * 🔴 防刷新循环设计（5 层防护）：
 * 1. 空值跳过：userId 为空 / anon / undefined 时，一律不触发 reload
 *    （未登录态/游客态不存在"切换账号"概念，也避免 SDK profile 抖动）
 * 2. 首次挂载只记录基准，不 reload
 * 3. 只接受「非空 A → 非空 B」的明确切换才 reload
 *    （空 → 有 / 有 → 空 / 空 → 空 都不触发）
 * 4. reload 前先在 sessionStorage 写「pending_reload」标记
 *    如果页面 reload 后 2 秒内又触发 reload，判定为循环，直接停止
 * 5. 防抖 300ms：profile 短时间多次抖动时，只按最终值比对一次
 */

const SESSION_KEY = 'uid_guard_last_id';
const LOOP_GUARD_KEY = 'uid_guard_pending_reload';
const LOOP_GUARD_TTL = 3000; // 3 秒内重复触发视为循环

function isValidUserId(id: string | undefined | null): id is string {
  if (!id) return false;
  if (id === 'anon') return false;
  if (id === 'undefined') return false;
  if (id === 'null') return false;
  return id.length > 0;
}

export default function UserIdentityGuard() {
  const profile = useCurrentUserProfile();
  const initialized = useRef(false);
  const debounceTimer = useRef<number | null>(null);

  useEffect(() => {
    // 用 SDK 同步方法兜底，但只在 profile 为空时才用
    const currentUserId = profile?.user_id || getCurrentUserId();

    // 防抖：短时间多次抖动只处理最后一次
    if (debounceTimer.current) {
      window.clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = window.setTimeout(() => {
      handleUserIdChange(currentUserId);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        window.clearTimeout(debounceTimer.current);
      }
    };
  }, [profile?.user_id]);

  function handleUserIdChange(currentUserId: string) {
    try {
      // 第 1 层：空值一律不触发，也不更新基准
      // （防止 SDK profile 还在加载 / 未登录态的空值抖动）
      if (!isValidUserId(currentUserId)) {
        return;
      }

      const lastUserId = sessionStorage.getItem(SESSION_KEY);

      // 第 2 层：首次挂载只记录基准值
      if (!initialized.current) {
        initialized.current = true;
        sessionStorage.setItem(SESSION_KEY, currentUserId);
        return;
      }

      // 第 3 层：只接受「非空 A → 非空 B」的明确切换
      if (!isValidUserId(lastUserId)) {
        // 上次是空值（可能 SDK 延迟加载），更新基准但不 reload
        sessionStorage.setItem(SESSION_KEY, currentUserId);
        return;
      }

      if (lastUserId === currentUserId) {
        // 同一个用户，什么都不做
        return;
      }

      // 第 4 层：循环检测
      // 如果上次 reload 后 3 秒内又触发 reload，说明可能是循环，停止
      const pendingReloadRaw = sessionStorage.getItem(LOOP_GUARD_KEY);
      if (pendingReloadRaw) {
        const pendingTime = parseInt(pendingReloadRaw, 10);
        const now = Date.now();
        if (now - pendingTime < LOOP_GUARD_TTL) {
          logger.error(
            `[UserIdentityGuard] ⚠️ 检测到可能的刷新循环: ${lastUserId} -> ${currentUserId}, 已停止`,
          );
          // 更新基准，停止循环
          sessionStorage.setItem(SESSION_KEY, currentUserId);
          sessionStorage.removeItem(LOOP_GUARD_KEY);
          return;
        }
      }

      // 第 5 层：写入 pending 标记 + 执行 reload
      logger.info(
        `[UserIdentityGuard] 检测到账号切换: ${lastUserId} -> ${currentUserId}, 正在刷新页面...`,
      );
      sessionStorage.setItem(LOOP_GUARD_KEY, String(Date.now()));
      sessionStorage.setItem(SESSION_KEY, currentUserId);

      setTimeout(() => {
        window.location.reload();
      }, 50);
    } catch (err) {
      // sessionStorage 不可用或任何异常 → 静默，不让守卫破坏应用
      logger.warn('[UserIdentityGuard] 守卫异常，已跳过:', String(err));
    }
  }

  return null;
}
